import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { generatePath, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import AddRemoveControl from '@/components/common/AddRemoveControl';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import RouterLink from '@/components/common/RouterLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import EmailLabels from '@/constants/ui/EmailLabels';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { searchForConstituentsByEmail } from '@/services/constituentServices';
import { VALID_EMAIL_REG } from '@/utils/emails';
import ConstituentField from '../constants/ConstituentField';
import LockedFormHelperTextProps from '../constants/LockedFormHelperTextProps';
import LockedSalesforceHelperText from './LockedSalesforceHelperText';
import PrimarySetterButton from './PrimarySetterButton';

const getNewEmail = (primary = false) => ({
  email: '',
  label: '',
  primary,
});

function EmailFields({ locked }) {
  const { constituentId } = useParams();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: ConstituentField.EMAILS,
  });
  const ownerOrgId = useOwnerOrgId();

  const checkUniqueness = React.useCallback(
    async (emailAddress) => {
      if (!emailAddress) return true;
      const response = await searchForConstituentsByEmail({
        ownerOrgId,
        params: { emailAddress, queryScope: 'email_address' },
      });

      const constituent = response?.data?.constituents?.[0];

      return (
        !constituent ||
        constituent.constituentId === constituentId || (
          <Alert icon={false} severity="warning">
            {translate('CONSTITUENT_EXISTS_IN_SYSTEM', {
              displayName: constituent.displayName,
              'link-to-constituent': () => (
                <RouterLink
                  to={generatePath(AuthRoutes.CONSTITUENT, {
                    constituentId: constituent.constituentId,
                  })}
                  target="_blank"
                >
                  {constituent.displayName}
                </RouterLink>
              ),
            })}
          </Alert>
        )
      );
    },
    [ownerOrgId],
  );

  React.useEffect(() => {
    if (!fields.length) {
      append(getNewEmail(true));
    }
  }, [fields]);

  return (
    <>
      {fields.map((item, i, arr) => (
        <React.Fragment key={item.id}>
          <div className="relative lg:col-span-3">
            <ControlledTextField
              control={control}
              disabled={locked}
              fullWidth
              label={translate('EMAIL_ADDRESS_LABEL')}
              name={`${ConstituentField.EMAILS}.${i}.email`}
              type="email"
              rules={{
                pattern: {
                  value: VALID_EMAIL_REG,
                  message: translate('INVALID_EMAIL_FORMAT_MESSAGE'),
                },
                validate: { checkUniqueness },
              }}
              error={!!errors[ConstituentField.EMAILS]?.[i]?.email}
              helperText={
                errors[ConstituentField.EMAILS]?.[i]?.email.message || (
                  <LockedSalesforceHelperText locked={locked} />
                )
              }
              {...(locked && {
                FormHelperTextProps: LockedFormHelperTextProps,
              })}
            />
            {arr.length > 1 && (
              <PrimarySetterButton
                className="absolute right-2 top-1.5"
                disabled={locked}
                index={i}
                name={ConstituentField.EMAILS}
              />
            )}
          </div>
          <div className="relative flex items-center">
            <FormControl className="flex-grow lg:w-full" disabled={locked}>
              <InputLabel>{translate('TYPE_LABEL')}</InputLabel>
              <ControlledSelect
                control={control}
                disabled={locked}
                name={`${ConstituentField.EMAILS}.${i}.label`}
                label={translate('TYPE_LABEL')}
              >
                {EmailLabels.map((val) => (
                  <MenuItem key={val} value={val}>
                    {capitalize(val)}
                  </MenuItem>
                ))}
              </ControlledSelect>

              {locked && (
                <FormHelperText {...LockedFormHelperTextProps}>
                  <LockedSalesforceHelperText locked />
                </FormHelperText>
              )}
            </FormControl>
            <AddRemoveControl
              className="flex-shrink-0 pl-4 lg:absolute lg:-right-16 lg:-mr-2"
              disabled={locked}
              onAdd={() => append(getNewEmail())}
              onRemove={() => remove(i)}
              showAdd={i === arr.length - 1}
              showRemove={arr.length > 1}
            />
          </div>
        </React.Fragment>
      ))}
    </>
  );
}

EmailFields.propTypes = {
  locked: PropTypes.bool.isRequired,
};

export default EmailFields;
